import {MediaAttachment, MESSAGE_TYPE} from './Common.types';
import {
  MediaMessageData,
  MESSAGE_CATEGORY,
  MESSAGE_RECEIVER_TYPE,
  MESSAGE_STATUS,
  TextMessageData,
} from './Message.types';

/**
 * Connection's Last Message Text's Data
 */
export type LastMessageTextData = Pick<TextMessageData, 'text' | 'resource'>;

/**
 * Connection's Last Message Media's Data
 */
export type LastMessageMediaData = Pick<
  MediaMessageData,
  'url' | 'resource'
> & {
  type: MESSAGE_TYPE;
  category: MESSAGE_CATEGORY;
  attachments: MediaAttachment[];
};

/**
 * Last Message Details
 */
export type LastMessage = {
  /**
   * Message Category {@link MESSAGE_CATEGORY}
   */
  category: MESSAGE_CATEGORY;
  /**
   * message id
   */
  conversationId: string;
  /**
   * Message Data
   *
   * - {@link LastMessageTextData}
   * - {@link LastMessageMediaData}
   */
  data: LastMessageTextData | LastMessageMediaData;
  /**
   * Message's ID
   */
  id: string;
  /**
   * receiver's id
   */
  receiver: string;
  /**
   * receiver's type {@link MESSAGE_RECEIVER_TYPE}
   */
  receiverType: MESSAGE_RECEIVER_TYPE;
  /**
   * message sent at
   */
  /**
   * sender's id
   */
  sender: string;
  /**
   * Message Sent At time
   */
  sentAt: number;
  /**
   * message's type {@link MESSAGE_TYPE}
   */
  type: MESSAGE_TYPE;
  /**
   * Message Updated At time
   */
  updatedAt: number;

  /**
   * message delivered at
   */
  deliveredAt?: number;
  /**
   * message read at
   */
  readAt?: number;

  // Required to handle OPTIMISTIC UI
  /**
   * temporary ID
   */
  tempId?: string;
  /**
   * Message status
   */
  status?: MESSAGE_STATUS;
};

/**
 * Friend's Details
 */
export type Connection = {
  /**
   * comet chat user id (this is the same as mongo id of the user document in internal database)
   */
  uid: string;
  /**
   * User's name
   */
  name: string;
  /**
   * User's avatar (Injected from the internal database)
   */
  avatar: string;
  /**
   * User's available status
   */
  status: 'online' | 'offline';
  /**
   * User's role `patient` | `therapist` | `prescriber`
   */
  role: string;
  /**
   * User's creation time
   */
  createdAt: number;
  /**
   * User's conversation id
   */
  conversationId: string;
  /**
   * User's last active time
   */
  lastActiveAt: number;
  /**
   * User's metadata
   */
  isSearched?: boolean; // this field added to verify the searched friends

  /**
   * User's chat status (Injected from the internal database)
   */
  isChatActive: boolean;
} & {
  /**
   * total unread message count
   */
  unreadMessageCount: number;
  /**
   * User's last message
   */
  lastMessage: LastMessage;
  /**
   * User's typing status
   */
  typing?: boolean;
};

/**
 * Connection Types
 */
export enum CONNECTION_TYPE {
  RECENT = 'recent',
  ALL = 'all',
}

/**
 * Comet Chat UsersList/FriendsList/Connections
 */
export interface Connections {
  /**
   * User's list
   */
  data: Connection[];
  /**
   * Pagination
   */
  nextRequest: {
    type: CONNECTION_TYPE;
    meta: {
      count: number;
      perPage: number;
      currentPage: number;
      totalPages: number;
      nextPage: number | null;
      hasMore: boolean;
    };
  };
  /**
   * Total
   */
  total: number;
}

/**
 * Normalized Comet Chat UsersList/FriendsList/Connections
 */
export type NormalizedConnections =
  | (Omit<Connections, 'data'> & {
      entities: {
        connections: {[key: string]: Connection};
      };
      result: string[];
    })
  | null;

export type ChatParticipant = {
  conversationId: string;
  uid: string;
  isChatActive?: boolean;
} | null;
